import React, { useEffect, useState } from 'react';

/**
 * 핸디로얄 — HANDY 매입 프로그램 (2026-09-18)
 *
 * 토큰이 두 가지라 헷갈리기 쉽다. 여기서 분명히 해둔다.
 *   HANDY  = 이더리움 ERC-20, MEXC 상장 — **회원이 보내는 것(입금)**
 *   wHANDY = 핸디체인, 앱 안에서 쓰는 것 — **회원이 받는 것(보상)**
 *
 * 화면은 두 단계다.
 *   ① 랜딩 — 프로그램이 뭔지 보여주고 '참여하기' 하나만 둔다
 *   ② 참여 — 보상 방식 고르고 → 내 주소로 보내면 끝
 *
 * 거래 해시를 옮겨적게 하지 않는다.
 * 회원마다 **자기 입금주소**가 있고, 거기로 HANDY 가 오면 감시기가 알아서 참여시킨다.
 * (그래도 안 잡힐 때를 대비해 해시로 직접 등록하는 길은 남겨뒀다)
 *
 * MEXC 에서 바로 빼오는 건 못 한다 — 거래소 계정을 우리가 만질 수 없다.
 * 대신 주소를 복사해 주고 MEXC 출금 화면을 열어주는 데까지가 할 수 있는 최선이다.
 */

type Deposit = {
  id: number; amount: number; total: number; daily: number;
  paid_days: number; payout_days?: number; paid: number;
  status: string; next_pay_date?: string; payout_currency?: string;
};

type Info = {
  enabled: boolean; rate: number; days: number;
  minUsd: number; minHandy: number | null; price: number | null;
  max: number; cap: number; used: number; left: number;
  myDeposits?: Deposit[];
};

const n = (v: any, d = 0) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });

export function RoyalPage({ authHeaders, onBack }: {
  authHeaders: () => Record<string, string>;
  onBack: () => void;
}) {
  const [info, setInfo] = useState<Info | null>(null);
  const [step, setStep] = useState<'landing' | 'join'>('landing');
  const [addr, setAddr] = useState<{ address: string; token?: string } | null>(null);
  const [cur, setCur] = useState<'whandy' | 'usdt'>('whandy');
  const [copied, setCopied] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [incoming, setIncoming] = useState<any[]>([]);
  const [manual, setManual] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [busy, setBusy] = useState(false);
  // wHANDY 직접 참여 (2026-10-01)
  const [coin, setCoin] = useState<'whandy' | 'handy'>('whandy');
  const [amt, setAmt] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  // 설명 그림은 기본으로 접어둔다. 처음 온 사람에게 그림 세 장은 벽이다.
  const [openHow, setOpenHow] = useState(false);

  const load = async () => {
    try {
      const j = await fetch('/api/royal/info', { headers: authHeaders() }).then(r => r.json());
      if (j?.success) setInfo(j);
      const e = await fetch('/api/royal/my-eth-deposits', { headers: authHeaders() }).then(r => r.json());
      if (e?.success) setIncoming(e.rows || []);
    } catch { /* 화면은 떠야 한다 */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // 참여 화면에 들어올 때 내 주소를 받아온다
  useEffect(() => {
    if (step !== 'join' || addr) return;
    (async () => {
      try {
        const j = await fetch('/api/royal/my-address', { headers: authHeaders() }).then(r => r.json());
        if (j?.success) {
          setAddr({ address: j.address, token: j.token });
          if (j.payoutCurrency) setCur(j.payoutCurrency);
        } else setMsg({ ok: false, text: j?.error || '입금주소를 받지 못했습니다' });
      } catch (e: any) { setMsg({ ok: false, text: e.message }); }
    })();
    /* eslint-disable-next-line */
  }, [step]);

  /** 보상 방식은 입금 전에 정해둔다 — 입금이 들어오면 이 값으로 참여된다 */
  const pickCur = async (v: 'whandy' | 'usdt') => {
    setCur(v);
    try {
      await fetch('/api/royal/prefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ payoutCurrency: v }),
      });
    } catch { /* 저장 실패해도 기본값(wHANDY)으로 돈다 */ }
  };

  /**
   * 주소를 클립보드에 넣는다.
   *
   * 클릭 직후 **기다리지 않고 바로** 불러야 한다. await 를 거치면 브라우저가 보기에
   * "사용자가 방금 누른 동작" 이 아니게 되고, 폰 브라우저는 그 상태의 복사를 막는다.
   * clipboard 가 아예 없는 브라우저도 있어서 옛 방식으로 한 번 더 시도한다.
   */
  const copyAddr = () => {
    const v = addr?.address;
    if (!v) return false;

    const ok = () => { setCopied(true); setTimeout(() => setCopied(false), 2500); return true; };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(v).then(ok).catch(() => fallbackCopy(v) || warn());
      return true;
    }
    return fallbackCopy(v) ? ok() : warn();
  };

  /** clipboard 가 막힌 브라우저용. 화면 밖에 칸을 만들어 복사한다. */
  const fallbackCopy = (v: string) => {
    try {
      const el = document.createElement('textarea');
      el.value = v;
      el.setAttribute('readonly', '');
      el.style.cssText = 'position:fixed;left:-9999px;opacity:0';
      document.body.appendChild(el);
      el.select();
      el.setSelectionRange(0, v.length);
      const done = document.execCommand('copy');
      document.body.removeChild(el);
      return done;
    } catch { return false; }
  };

  const warn = () => {
    setMsg({ ok: false, text: '복사가 안 됩니다. 위 주소를 길게 눌러 직접 복사해주세요.' });
    return false;
  };

  /**
   * MEXC 출금 화면을 연다.
   *
   * 주소 자동 입력은 **불가능**하다. MEXC 는 우리 사이트가 아니고,
   * 브라우저는 한 사이트가 다른 사이트의 입력칸을 건드리는 걸 막는다.
   * 그래서 복사해 주고 여는 데까지가 할 수 있는 전부다.
   */
  const openMexc = () => {
    copyAddr();                       // 기다리지 않는다 — 기다리면 복사가 막힌다
    window.open('https://www.mexc.com/assets/withdraw/HANDY', '_blank', 'noopener');
    setMsg({
      ok: true,
      text: '주소를 복사했습니다.\n' +
            '열린 MEXC 웹 화면에서 바로 하시면 됩니다 (앱으로 갈 필요 없습니다).\n' +
            '① Address 칸에 길게 눌러 붙여넣기\n② Network 는 Ethereum(ERC20)\n③ 금액 넣고 출금',
    });
  };

  const submitManual = async () => {
    const h = txHash.trim();
    if (!/^0x[a-fA-F0-9]{64}$/.test(h)) {
      return setMsg({ ok: false, text: '거래 해시를 정확히 넣어주세요 (0x 로 시작하는 66자)' });
    }
    setBusy(true); setMsg({ ok: true, text: '이더리움에서 확인 중입니다…' });
    try {
      const j = await fetch('/api/royal/deposit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ethTxHash: h, payoutCurrency: cur, agreed: true }),
      }).then(r => r.json());
      if (!j.success) throw new Error(j.error || '등록에 실패했습니다');
      setMsg({ ok: true, text: `참여 완료! ${n(j.amount)} HANDY · ${j.firstPayDate} 부터 지급` });
      setTxHash(''); load();
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
    setBusy(false);
  };

  const submitWhandy = async () => {
    const v = Number(amt);
    if (!(v >= 500000)) return setMsg({ ok: false, text: '최소 500,000 wHANDY 부터 참여할 수 있습니다' });
    setJoinBusy(true);
    try {
      const j = await fetch('/api/royal/deposit-whandy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ amount: v, payoutCurrency: 'whandy', agreed: true }),
      }).then(r => r.json());
      if (!j.success) throw new Error(j.error || '참여에 실패했습니다');
      setMsg({ ok: true, text: `참여 완료! ${n(j.amount)} wHANDY · ${j.firstPayDate} 부터 매일 ${n(j.daily, 2)} 지급` });
      setAmt(''); await load();
      setStep('landing');                                   // 참여 현황이 맨 위에 보인다
      try { window.dispatchEvent(new CustomEvent('handy:balance-refresh')); } catch {}
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
    setJoinBusy(false);
  };

  const mine = (info?.myDeposits || []).filter(d => String(d.status) === 'active');

  const box: React.CSSProperties = {
    background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 16,
    marginBottom: 12, color: '#111827', boxShadow: '0 1px 3px rgba(17,24,39,.05)',
  };
  const big: React.CSSProperties = {
    width: '100%', padding: 16, fontSize: 16, fontWeight: 800, border: 0,
    borderRadius: 14, color: '#fff', background: '#FF6B00', cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(255,107,0,.3)',
  };
  const Img = ({ name, alt, lazy }: { name: string; alt: string; lazy?: boolean }) => (
    <div style={{ borderRadius: 14, overflow: 'hidden', marginBottom: 12 }}>
      <picture>
        <source srcSet={`/royal/${name}.webp?v=2`} type="image/webp" />
        <img src={`/royal/${name}.jpg?v=2`} alt={alt}
             style={{ width: '100%', height: 'auto', display: 'block' }}
             loading={lazy ? 'lazy' : undefined} />
      </picture>
    </div>
  );

  return (
    <div style={{ padding: 14, paddingBottom: 90, background: '#F7F8FA', minHeight: '100vh', color: '#111827' }}>
      <button onClick={() => (step === 'join' ? setStep('landing') : onBack())}
        style={{ background: 'none', border: 0, fontSize: 15, color: '#6B7280', padding: '4px 0 12px' }}>
        ‹ {step === 'join' ? '프로그램 안내' : '뒤로'}
      </button>

      {/* ───────────── 랜딩 ───────────── */}
      {step === 'landing' && (
        <>
          <Img name="royal-banner" alt="Handy Royal — HANDY 매입 프로그램" />

          {/* 참여 중인 분에게는 내 현황이 제일 궁금한 것이다. 맨 위에 둔다. */}
          {mine.length > 0 && (
            <div style={{ ...box, borderColor: '#FFD9BC', background: '#FFF8F3' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#C2410C', marginBottom: 10 }}>
                참여 중
              </div>
              {mine.map(d => {
                const total = Number(d.payout_days || info?.days || 365);
                const done = Number(d.paid_days || 0);
                const pct = Math.min(100, Math.round((done / total) * 100));
                return (
                  <div key={d.id} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                      <b>{n(d.amount)} HANDY</b>
                      <span style={{ color: '#6B7280' }}>{done} / {total}일</span>
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: '#FFE4D0', margin: '6px 0' }}>
                      <div style={{ width: pct + '%', height: '100%', borderRadius: 3, background: '#FF6B00' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      {([['매일 지급', '+' + n(Number((d as any).daily || (d as any).daily_amount ||
                            (Number(d.amount) * (info?.rate || 240) / 100 / total)), 2)],
                         ['지금까지 받음', n(d.paid, 2)],
                         ['남은 일수', (total - done) + '일']] as const).map(([k2, v2]) => (
                        <div key={k2} style={{ flex: 1, textAlign: 'center', background: '#fff',
                          border: '1px solid #FFE4D0', borderRadius: 10, padding: '7px 2px' }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: '#C2410C' }}>{v2}</div>
                          <div style={{ fontSize: 10, color: '#6B7280', marginTop: 1 }}>{k2}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 6 }}>
                      {d.next_pay_date ? `다음 지급 ${String(d.next_pay_date).slice(0, 10)} · ` : ''}
                      수익은 wHANDY 로 매일 자동 지급됩니다
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 핵심 숫자와 진짜 버튼을 맨 위에. 그림을 다 지나야 버튼이 나오면 아무도 안 누른다. */}
          {info && !info.enabled && (
            <div style={{ ...box, textAlign: 'center', color: '#6B7280' }}>
              아직 참여 기간이 아닙니다. 곧 공지됩니다.
            </div>
          )}
          {info && info.enabled && info.left <= 0 && (
            <div style={{ ...box, textAlign: 'center', color: '#6B7280' }}>
              이번 회차 모집이 마감됐습니다.
            </div>
          )}
          {info && info.enabled && info.left > 0 && (
            <div style={box}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                {[
                  { k: '돌려받는 총액', v: info.rate + '%' },
                  { k: '지급 기간', v: info.days + '일' },
                  { k: '최소 참여', v: n((info as any).minJoin || 500000) },
                ].map(c => (
                  <div key={c.k} style={{
                    flex: 1, textAlign: 'center', background: '#FFF4EC',
                    borderRadius: 12, padding: '10px 4px',
                  }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#FF6B00' }}>{c.v}</div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{c.k}</div>
                  </div>
                ))}
              </div>

              <div style={{ fontSize: 13, lineHeight: 1.75, color: '#374151', marginBottom: 12 }}>
                보유하신 <b>HANDY</b> 를 맡기시면 원금 100% 와 수익 {info.rate - 100}% 를
                합쳐 <b>{info.days}일</b> 동안 매일 나누어 드립니다.
                <><br />최소 참여 수량은 {n((info as any).minJoin || 500000)} 개 입니다.</>
              </div>

              <button onClick={() => setStep('join')} style={big}>참여하기</button>

              <div style={{ marginTop: 12 }}>
                <div style={{ height: 6, borderRadius: 3, background: '#F1F2F4' }}>
                  <div style={{
                    width: Math.min(100, Math.round(((info.cap - info.left) / (info.cap || 1)) * 100)) + '%',
                    height: '100%', borderRadius: 3, background: '#FF6B00',
                  }} />
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', marginTop: 6 }}>
                  남은 자리 {n(info.left)} / {n(info.cap)} HANDY
                </div>
              </div>
            </div>
          )}

          {/* 설명 그림은 한 장만, 그것도 접어둔다. 궁금한 사람만 펼쳐 본다. */}
          <button
            onClick={() => setOpenHow(v => !v)}
            style={{
              width: '100%', padding: 14, background: '#fff', border: '1px solid #E5E7EB',
              borderRadius: 14, fontSize: 14, fontWeight: 700, color: '#374151',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: openHow ? 10 : 0, cursor: 'pointer',
            }}
          >
            <span>어떤 프로그램인가요?</span>
            <span style={{ color: '#9CA3AF' }}>{openHow ? '접기 ⌃' : '펼치기 ⌄'}</span>
          </button>
          {openHow && (
            <Img name="royal-steps" alt="HANDY 매입 프로그램 · 거래소 매입 · 365일 데일리 지급 · 소각 발표" lazy />
          )}
        </>
      )}

      {/* ───────────── 참여 ───────────── */}
      {step === 'join' && info?.enabled && (
        <>
          <div style={{ background: '#0F172A', borderRadius: 16, padding: '18px 16px',
                        marginBottom: 12, color: '#fff' }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 3, color: '#FDBA74' }}>
              HANDY ROYAL
            </div>
            <div style={{ fontSize: 23, fontWeight: 900, marginTop: 4 }}>재매입 프로그램</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
              {['총 ' + info.rate + '% 지급', info.days + '일 매일 지급', '수익은 wHANDY'].map(t => (
                <span key={t} style={{ fontSize: 12, fontWeight: 700, padding: '5px 10px',
                                       borderRadius: 999, background: 'rgba(255,255,255,.14)' }}>
                  {t}
                </span>
              ))}
            </div>
          </div>
          {/* ① 참여 코인 — wHANDY(앱 잔액) / HANDY(이더리움 입금). 수익은 wHANDY 매일 지급 */}
          <div style={box}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>① 참여할 코인 선택</div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 10 }}>
              수익은 <b style={{ color: '#111827' }}>wHANDY 로 매일</b> 지급됩니다.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {([['whandy', 'wHANDY', '앱 지갑 잔액으로 바로'],
                 ['handy', 'HANDY', '이더리움(ERC-20) 입금']] as const).map(([k, t, d]) => (
                <button key={k} type="button" onClick={() => setCoin(k)}
                  style={{
                    flex: 1, padding: '11px 9px', borderRadius: 12, textAlign: 'left',
                    border: coin === k ? '2px solid #FF6B00' : '1px solid #E5E7EB',
                    background: coin === k ? '#FFF7ED' : '#fff', cursor: 'pointer',
                  }}>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>{t}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>{d}</div>
                </button>
              ))}
            </div>
          </div>

          {coin === 'whandy' && info && (
            <div style={box}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>② 수량 입력</div>
              <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 10 }}>
                최소 500,000 wHANDY 부터 참여할 수 있습니다.
              </div>
              <input value={amt ? Number(amt).toLocaleString() : ''}
                onChange={e => setAmt(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric" placeholder="최소 500,000"
                style={{ width: '100%', padding: '14px 16px', borderRadius: 14,
                         border: '2px solid #FED7AA', fontSize: 20, fontWeight: 800,
                         boxSizing: 'border-box', background: '#FFFBF7', color: '#111827',
                         textAlign: 'center', outline: 'none' }} />
              <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  보유 {info.balance != null ? n(Math.floor(info.balance)) : '-'} wHANDY
                </span>
                <button type="button"
                  onClick={() => setAmt(String(Math.max(0, Math.floor(info.balance || 0))))}
                  style={{ fontSize: 12, fontWeight: 800, color: '#FF6B00', background: '#FFF4EC',
                           border: 0, borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
                  전액 입력
                </button>
              </div>
              {Number(amt) >= 500000 && (() => {
                const v = Number(amt);
                const total = v * info.rate / 100;
                const daily = total / info.days;
                const be = Math.ceil(info.days * 100 / info.rate);
                const W = 300, H = 130, pad = 10;
                const x = (d: number) => pad + (W - pad * 2) * d / info.days;
                const y = (vv: number) => H - pad - (H - pad * 2) * vv / total;
                return (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      {([['총 수령액', n(total)], ['매일 지급', n(daily, 2)], ['지급 기간', info.days + '일']] as const)
                        .map(([k2, v2]) => (
                        <div key={k2} style={{ flex: 1, textAlign: 'center', background: '#FFF4EC',
                                               borderRadius: 12, padding: '10px 4px' }}>
                          <div style={{ fontSize: 15, fontWeight: 800, color: '#FF6B00' }}>{v2}</div>
                          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{k2}</div>
                        </div>
                      ))}
                    </div>
                    {/* 누적 지급 그래프 — 매일 같은 금액이라 직선, 원금 회수 시점 표시 */}
                    <svg viewBox={`0 0 ${W} ${H}`}
                      style={{ width: '100%', background: '#F9FAFB', borderRadius: 12 }}>
                      <line x1={x(0)} y1={y(0)} x2={x(info.days)} y2={y(total)}
                        stroke="#FF6B00" strokeWidth="2.5" />
                      <line x1={pad} y1={y(v)} x2={W - pad} y2={y(v)}
                        stroke="#9CA3AF" strokeWidth="1" strokeDasharray="4 3" />
                      <circle cx={x(be)} cy={y(v)} r="4" fill="#16A34A" />
                      <text x={Math.min(x(be) + 6, W - 95)} y={y(v) - 7} fontSize="10"
                        fill="#16A34A" fontWeight="700">원금 회수 ~{be}일차</text>
                      <text x={pad} y={y(v) + 13} fontSize="10" fill="#6B7280">원금 {n(v)}</text>
                      <text x={W - pad} y={y(total) + 13} fontSize="10" fill="#FF6B00"
                        textAnchor="end" fontWeight="700">총 {n(total)}</text>
                    </svg>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8, lineHeight: 1.6 }}>
                      내일부터 매일 <b>{n(daily, 2)} wHANDY</b> 씩 {info.days}일간 지급됩니다.
                      약 {be}일차에 원금을 모두 회수하고, 이후 받는 건 전부 수익입니다.
                    </div>
                  </div>
                );
              })()}
              <button onClick={submitWhandy} disabled={joinBusy || Number(amt) < 500000}
                style={{ ...big, marginTop: 12,
                         opacity: joinBusy || Number(amt) < 500000 ? .5 : 1 }}>
                {joinBusy ? '처리 중…' : '재매입 참여하기'}
              </button>
            </div>
          )}


          {coin === 'handy' && (<>
          {/* ② 내 주소로 보내기 */}
          <div style={box}>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>② 내 입금주소로 HANDY 보내기</div>
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, marginBottom: 10 }}>
              아래는 <b style={{ color: '#111827' }}>회원님 전용 주소</b>입니다.
              여기로 들어오면 자동으로 참여 처리됩니다 — 따로 하실 게 없습니다.
            </div>

            <div onClick={copyAddr}
              style={{
                padding: 12, borderRadius: 10, cursor: 'pointer',
                background: copied ? '#F0FDF4' : '#F9FAFB',
                border: `1px solid ${copied ? '#BBF7D0' : '#E5E7EB'}`,
                fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
                wordBreak: 'break-all', lineHeight: 1.5,
              }}>
              {addr?.address || '주소를 불러오는 중…'}
              {addr?.address && (
                <div style={{
                  marginTop: 6, fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                  color: copied ? '#166534' : '#FF6B00',
                }}>
                  {copied ? '✓ 복사했습니다' : '탭하면 복사됩니다'}
                </div>
              )}
            </div>

            <button onClick={openMexc} disabled={!addr?.address}
              style={{
                width: '100%', marginTop: 10, padding: 13, fontSize: 15, fontWeight: 700,
                borderRadius: 12, background: '#fff', color: '#FF6B00',
                border: '1px solid #FF6B00', cursor: addr?.address ? 'pointer' : 'default',
                opacity: addr?.address ? 1 : .5,
              }}>
              MEXC 출금 화면 열기 (주소 자동 복사)
            </button>
            <div style={{ marginTop: 10, padding: 12, borderRadius: 10,
                          background: '#FFFBEB', border: '1px solid #FDE68A',
                          fontSize: 13, color: '#92400E', lineHeight: 1.7 }}>
              네트워크는 꼭 <b>이더리움(ERC-20)</b> · 최소 <b>{n((info as any).minJoin || 500000)} HANDY</b>
              <br />입금되면 자동으로 참여 처리됩니다.
            </div>
          </div>

          </>)}

          {/* ③ 조건 */}
          <div style={box}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>③ 조건</div>
            <div style={{
              padding: 12, borderRadius: 10, background: '#FFF7ED',
              border: '1px solid #FED7AA', fontSize: 13, lineHeight: 1.7,
            }}>
              참여 수량의 <b>{info.rate}%</b> 를 <b>{info.days}일</b> 동안 매일 나누어 받습니다.<br />
              <span style={{ color: '#6B7280' }}>원금은 지급액에 포함되어 돌려받습니다 · 중도 해지 불가</span>
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 10, lineHeight: 1.6 }}>
              참여 시 위 조건에 동의한 것으로 봅니다.
            </div>
          </div>

          {/* 들어온 입금 상태 */}
          {incoming.length > 0 && (
            <div style={box}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>입금 확인 중</div>
              {incoming.map((r, i) => (
                <div key={i} style={{ padding: '9px 0', borderTop: i ? '1px solid #F3F4F6' : 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                    <b>{n(r.amount)} HANDY</b>
                    <span style={{
                      fontSize: 12, fontWeight: 700,
                      color: r.status === 'joined' ? '#166534'
                        : r.status === 'held' ? '#B45309' : '#6B7280',
                    }}>
                      {r.status === 'joined' ? '참여 완료'
                        : r.status === 'held' ? '확인 필요' : '처리 중'}
                    </span>
                  </div>
                  {r.note && (
                    <div style={{ fontSize: 12, color: '#B45309', marginTop: 3 }}>{r.note}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 안 잡힐 때를 위한 수동 등록 */}
          <div style={{ ...box, padding: 0, overflow: 'hidden' }}>
            <button type="button" onClick={() => setManual(v => !v)}
              style={{
                width: '100%', padding: '13px 14px', background: 'none', border: 0,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                font: 'inherit', color: 'inherit', cursor: 'pointer',
              }}>
              <span style={{ fontSize: 13, color: '#6B7280' }}>보냈는데 안 잡히나요?</span>
              <span style={{ opacity: .55, fontSize: 13 }}>{manual ? '접기 ▲' : '직접 등록 ▼'}</span>
            </button>
            {manual && (
              <div style={{ padding: '0 14px 14px' }}>
                <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6, marginBottom: 8 }}>
                  보내신 거래 해시를 넣으면 바로 확인해 드립니다.
                </div>
                <input value={txHash} onChange={e => setTxHash(e.target.value)}
                       placeholder="0x…" spellCheck={false}
                       style={{
                         width: '100%', padding: 12, fontSize: 13, boxSizing: 'border-box',
                         border: '1px solid #E5E7EB', borderRadius: 10,
                         fontFamily: 'ui-monospace, Menlo, monospace',
                       }} />
                <button onClick={submitManual} disabled={busy || !txHash.trim()}
                  style={{
                    width: '100%', marginTop: 9, padding: 12, fontSize: 14, fontWeight: 700,
                    border: 0, borderRadius: 10, color: '#fff',
                    background: (busy || !txHash.trim()) ? '#FBBF8A' : '#FF6B00',
                  }}>
                  {busy ? '확인 중…' : '거래 해시로 등록'}
                </button>
              </div>
            )}
          </div>

          {msg && (
            <div style={{
              padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 12,
              whiteSpace: 'pre-wrap', lineHeight: 1.6,
              background: msg.ok ? '#F0FDF4' : '#FEF2F2',
              color: msg.ok ? '#166534' : '#991B1B',
              border: `1px solid ${msg.ok ? '#BBF7D0' : '#FECACA'}`,
            }}>{msg.text}</div>
          )}
        </>
      )}

      {/* 내 참여 */}
      {info?.myDeposits && info.myDeposits.length > 0 && (
        <div style={box}>
          <div style={{ fontWeight: 800, marginBottom: 10 }}>내 참여</div>
          {info.myDeposits.map(d => {
            const days = Number(d.payout_days || info.days) || 1;
            const pct = Math.min(100, (Number(d.paid_days) / days) * 100);
            return (
              <div key={d.id} style={{ padding: '10px 0', borderTop: '1px solid #F3F4F6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                  <b>{n(d.amount)} HANDY</b>
                  <span style={{ color: '#6B7280' }}>
                    {d.paid_days}/{days}일 · {d.payout_currency === 'usdt' ? 'USDT' : 'wHANDY'}
                  </span>
                </div>
                <div style={{ height: 6, background: '#F3F4F6', borderRadius: 99, margin: '7px 0' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#FF6B00', borderRadius: 99 }} />
                </div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>
                  받은 금액 {n(d.paid, 2)} / {n(d.total)} · 하루 {n(d.daily, 4)}
                  {d.next_pay_date ? ` · 다음 ${String(d.next_pay_date).slice(0, 10)}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
